import {
  posthogFeatureFlags,
  relewiseRecommendations
} from '@/shared/constants/constants';
import {
  attachRowArrows,
  createRecommendationElement,
  createSourceDecision,
  excludeProductFromTarget,
  onRecommendationsSettled,
  onSlotViewed,
  type RecommendationProductType,
  type RecommendationType,
  type RecommendationVariant,
  resolveRecommendationVariant,
  startSlotTracking,
  trackSlotViewed,
  waitForRelewise
} from '@/shared/instances/relewise-recommendations';
import { loadSlider } from '@/shared/utils/swiper';

const { HIDDEN_CLASS, SOURCES, SECTION_TYPES, VARIANTS, PRODUCT_TYPES } =
  relewiseRecommendations;

const SELECTORS = {
  SLOT: '.js-async-recommended-products',
  TITLE: '.js-recommendations-title',
  SLIDER: '.js-recommended-products-slider-async',
  EMPTY_MESSAGE: 'js-empty-recommendations-msg'
} as const;

const CLASSES = {
  SLOT_MARGIN: 'mb-8'
} as const;

const RELEWISE_WIDGETS = {
  [PRODUCT_TYPES.PATTERN]: {
    type: 'products_viewed_after_viewing_product',
    location: relewiseRecommendations.PDP_PATTERN_LOCATION
  },
  [PRODUCT_TYPES.OTHER]: {
    type: 'purchased_with_product',
    location: relewiseRecommendations.PDP_LOCATION
  }
} as const satisfies Record<
  RecommendationProductType,
  { type: RecommendationType; location: string }
>;

// The slot stays in the DOM at zero height rather than being removed, so the
// slot-viewed observer still fires for visitors whose slot is empty.
const hideSlot = (element: HTMLElement) => {
  element.parentElement
    ?.querySelector<HTMLElement>(SELECTORS.TITLE)
    ?.classList.add(HIDDEN_CLASS);
  element.replaceChildren();
  element.classList.remove(CLASSES.SLOT_MARGIN);
};

const renderShopify = async (element: HTMLElement) => {
  const { url } = element.dataset;
  if (!url)
    throw new Error('URL could not be found for loading recommended products');
  const request = await fetch(url);
  const section = await request.text();

  if (section.includes(SELECTORS.EMPTY_MESSAGE)) {
    hideSlot(element);
    return;
  }

  // Override section content (loading placeholder)
  element.innerHTML = section;

  const sliderWrapper = element.querySelector<HTMLElement>(SELECTORS.SLIDER);
  if (sliderWrapper) {
    // We use the section id from .js-async-recommended-products to scope the slider initialization
    // to avoid initializing the slider multiple times
    loadSlider(sliderWrapper, {});
  }
};

const renderRelewise = (
  element: HTMLElement,
  entityId: string,
  productType: RecommendationProductType
) => {
  const { type, location } = RELEWISE_WIDGETS[productType];
  const target = `${relewiseRecommendations.PDP_TARGET_PREFIX}-${element.dataset.sectionId}`;
  excludeProductFromTarget(target, entityId);

  const widget = createRecommendationElement({
    type,
    location,
    count:
      Number(element.dataset.limit) || relewiseRecommendations.DEFAULT_COUNT,
    target,
    productId: entityId,
    scrollRow: true
  });

  // The widget fetches on connect, so it is attached once inside a hidden
  // wrapper and revealed in place - moving it later would refetch.
  const wrapper = document.createElement('div');
  wrapper.classList.add(HIDDEN_CLASS);
  wrapper.appendChild(widget);

  onRecommendationsSettled(widget, (hasResults) => {
    if (hasResults) {
      Array.from(element.children).forEach((child) => {
        if (child !== wrapper) child.remove();
      });
      wrapper.classList.remove(HIDDEN_CLASS);
      attachRowArrows({
        container: wrapper,
        widget,
        template:
          element.parentElement?.querySelector<HTMLTemplateElement>(
            relewiseRecommendations.ARROWS_TEMPLATE_SELECTOR
          ) ?? null
      });
    } else {
      hideSlot(element);
    }
  });
  element.appendChild(wrapper);
};

const loadSlot = async (element: HTMLElement) => {
  const { decide: decideSource, decided: decidedSource } =
    createSourceDecision();

  try {
    const productType: RecommendationProductType =
      element.dataset.entityType === PRODUCT_TYPES.PATTERN
        ? PRODUCT_TYPES.PATTERN
        : PRODUCT_TYPES.OTHER;
    const variant: RecommendationVariant | null =
      await resolveRecommendationVariant(
        posthogFeatureFlags.RECO_PDP_ALSO_BOUGHT
      );

    if (variant) {
      onSlotViewed(element, async () => {
        trackSlotViewed({
          flagKey: posthogFeatureFlags.RECO_PDP_ALSO_BOUGHT,
          variant,
          location: relewiseRecommendations.PDP_LOCATION,
          source: await decidedSource,
          productType
        });
      });
    }

    const entityId = element.dataset.entityId ?? '';
    const useRelewise =
      variant === VARIANTS.TEST && !!entityId && (await waitForRelewise());

    if (useRelewise) {
      decideSource(SOURCES.RELEWISE);
      startSlotTracking(
        element,
        element.dataset.sectionId ?? '',
        SECTION_TYPES.RELEWISE
      );
      renderRelewise(element, entityId, productType);
      return;
    }

    decideSource(SOURCES.SHOPIFY);
    if (variant)
      startSlotTracking(
        element,
        element.dataset.sectionId ?? '',
        SECTION_TYPES.SHOPIFY
      );
    await renderShopify(element);
  } catch (error) {
    // Fail silently
    console.error(error);
    decideSource(SOURCES.SHOPIFY);
  }
};

const loadRecommendedProducts = () => {
  document
    .querySelectorAll<HTMLElement>(SELECTORS.SLOT)
    .forEach((element) => void loadSlot(element));
};

loadRecommendedProducts();
